/**
 * 功能标记命名空间定义
 * 使用分层结构，支持更灵活的配置和简洁的命名
 */
export const FeatureFlags = {
  vesselTrackingMenu: 'vesselTrackingMenu',
  vesselCard: {
    favorite: 'vesselCard.favorite',
    basicInfo: {
      management: 'vesselCard.basicInfo.management',
      sanctions: 'vesselCard.basicInfo.sanctions',
      modification: 'vesselCard.basicInfo.modification',
      sameNameShips: 'vesselCard.basicInfo.sameNameShips',
      sisterShips: 'vesselCard.basicInfo.sisterShips',
      safetyManning: 'vesselCard.basicInfo.safetyManning',
      valuation: 'vesselCard.basicInfo.valuation',
    },
    management: {
      shipyardAnalysis: 'vesselCard.management.shipyardAnalysis',
      companyAiSummary: 'vesselCard.management.companyAiSummary',
      companyRiskTab: 'vesselCard.management.companyRiskTab',
    },
    historicalTrajectory: {
      playback: 'vesselCard.historicalTrajectory.playback',
    },
    riskInfo: 'vesselCard.riskInfo',
    sanctions: 'vesselCard.sanctions', 
    violation: 'vesselCard.violation',
  },
  vesselSearch: {
    advanced: 'vesselSearch.advanced',
  },
  mapLayer: {
    weather: 'mapLayer.weather',
  },
  mapControl: {
    miniMap: 'mapControl.miniMap',
    polylineMeasure: 'mapControl.polylineMeasure',
  },
} as const;

/**
 * 从嵌套对象中提取所有路径的类型
 */
type NestedPaths<T> = T extends string
  ? T
  : T extends Record<string, unknown>
  ? {
      [K in keyof T]: T[K] extends string
        ? T[K]
        : T[K] extends Record<string, unknown>
        ? NestedPaths<T[K]>
        : never;
    }[keyof T]
  : never;

/**
 * 功能标记路径类型
 */
export type FeatureFlagPath = NestedPaths<typeof FeatureFlags>;

/**
 * 工具函数：获取功能标记路径
 * 支持点号分隔的路径访问，如 getFeaturePath('vesselCard.riskInfo')
 */
export function getFeaturePath(path: FeatureFlagPath): string {
  return path;
}

/**
 * 工具函数：检查是否为有效的功能标记路径
 */
export function isValidFeaturePath(path: string): path is FeatureFlagPath {
  const collectPaths = (value: string | Record<string, unknown>): string[] => {
    if (typeof value === 'string') return [value];
    return Object.values(value).flatMap((item) =>
      typeof item === 'string' ? [item] : collectPaths(item as Record<string, unknown>)
    );
  };

  return Object.values(FeatureFlags).flatMap(collectPaths).includes(path);
}

/**
 * 工具函数：根据模块名获取所有功能路径
 */
export function getModuleFeaturePaths(moduleName: keyof typeof FeatureFlags): string[] {
  const module = FeatureFlags[moduleName];
  if (typeof module === 'string') {
    return [module];
  }
  return Object.values(module).filter((value): value is string => typeof value === 'string');
}
